import React from "react";
import { Link, useParams, Navigate, useLocation } from "react-router-dom";
import { ArrowUpRight, Users, Mail, Phone, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { useFetch } from "@/lib/useData";
import { ASSET } from "@/lib/api";

function Eyebrow({ children }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-teal">
      <span className="h-px w-6 bg-teal" />{children}
    </span>
  );
}

export default function Team() {
  const { data: list, loading } = useFetch("/team-members");
  const items = (list || []).filter(Boolean);

  return (
    <>
      <SEO
        title="Our team | Infinity Medical Group"
        description="Meet the clinicians, scientists and pharmacists behind Infinity Medical Group's integrated healthcare network in Somalia."
        path="/team"
      />
      <Breadcrumbs />

      <section className="bg-white pt-4 pb-12" data-testid="team-hero">
        <div className="container">
          <Eyebrow>The IMG team</Eyebrow>
          <h1 className="mt-5 font-display font-bold text-4xl md:text-6xl text-navy-800 tracking-tight max-w-3xl">
            The people behind integrated care in Somalia.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-navy-700/75">
            Infinity Medical Group is led by a multidisciplinary team across our medical, diagnostic and pharmacy divisions.
          </p>
        </div>
      </section>

      <section className="bg-soft-gray py-16 lg:py-24">
        <div className="container">
          {loading ? (
            <p className="text-navy-700/60 text-sm">Loading…</p>
          ) : items.length === 0 ? (
            <div data-testid="team-empty" className="max-w-2xl mx-auto rounded-3xl bg-white border border-navy-50 p-10 lg:p-14 text-center">
              <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-teal/10 text-teal">
                <Users className="h-6 w-6" />
              </div>
              <h2 className="mt-6 font-display font-semibold text-2xl md:text-3xl text-navy-800">
                Meet our team — coming soon
              </h2>
              <p className="mt-4 text-navy-700/80 max-w-lg mx-auto">
                We are preparing detailed profiles for the clinicians and specialists who lead each division. In the meantime, reach our team directly for any consultation, second opinion or partnership enquiry.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/contact" data-testid="team-empty-cta-contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors">
                  Contact our team <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/booking" data-testid="team-empty-cta-booking" className="inline-flex items-center gap-2 px-6 py-3 rounded-full ring-1 ring-navy/15 text-navy text-sm font-semibold hover:bg-navy hover:text-white transition-colors">
                  Book a consultation
                </Link>
              </div>
            </div>
          ) : (
            <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((m) => (
                <Item key={m.slug || m.id}>
                  <Link
                    to={`/team/${m.slug}`}
                    data-testid={`team-card-${m.slug}`}
                    className="group block rounded-2xl bg-white border border-navy-50 hover:shadow-card transition-shadow overflow-hidden"
                  >
                    {m.photo_media?.url ? (
                      <div className="aspect-[4/5] bg-navy-50 overflow-hidden">
                        <img
                          src={ASSET(m.photo_media.url)}
                          alt={m.photo_media.alt || m.name}
                          loading="lazy"
                          className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                        />
                      </div>
                    ) : (
                      <div className="aspect-[4/5] bg-gradient-to-br from-navy via-navy-dark to-teal flex items-center justify-center text-white/40 font-display text-6xl">
                        {(m.name || "?").slice(0, 1)}
                      </div>
                    )}
                    <div className="p-6">
                      {m.specialty && (
                        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-teal">{m.specialty}</span>
                      )}
                      <h3 className="mt-2 font-display font-semibold text-lg text-navy-800 group-hover:text-teal transition-colors">
                        {m.name}
                      </h3>
                      {m.role && <p className="mt-1 text-sm text-navy-700/75">{m.role}</p>}
                    </div>
                  </Link>
                </Item>
              ))}
            </Stagger>
          )}
        </div>
      </section>
    </>
  );
}

export function TeamMember() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const { data, error, loading } = useFetch(`/team-members/${slug}`, [slug]);
  if (loading) return <div className="min-h-screen pt-32" />;
  if (error || !data) return <Navigate to="/team" replace />;

  const m = data;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: m.name,
    jobTitle: m.role || m.specialty,
    worksFor: { "@type": "MedicalOrganization", name: "Infinity Medical Group" },
    url: `https://infinitymg.so${pathname}`,
    ...(m.email ? { email: m.email } : {}),
    ...(m.photo_media?.url ? { image: m.photo_media.url } : {}),
  };

  return (
    <>
      <SEO
        title={`${m.name} | ${m.role || "Team"} | Infinity Medical Group`}
        description={(m.bio || `${m.name}, ${m.role || "team member"} at Infinity Medical Group in Somalia.`).replace(/<[^>]+>/g, "").slice(0, 158)}
        path={pathname}
        jsonLd={jsonLd}
      />
      <Breadcrumbs overrideLast={m.name} />
      <section className="bg-white pt-4 pb-12">
        <div className="container max-w-5xl">
          <Link to="/team" className="inline-flex items-center gap-1.5 text-sm text-navy-600 hover:text-teal">
            <ArrowLeft className="h-4 w-4" /> Back to team
          </Link>
          <div className="mt-6 grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              {m.photo_media?.url ? (
                <img
                  src={ASSET(m.photo_media.url)}
                  alt={m.photo_media.alt || m.name}
                  className="w-full aspect-[4/5] object-cover rounded-3xl"
                />
              ) : (
                <div className="aspect-[4/5] rounded-3xl bg-gradient-to-br from-navy via-navy-dark to-teal" />
              )}
            </div>
            <div className="lg:col-span-7">
              {m.specialty && <Eyebrow>{m.specialty}</Eyebrow>}
              <h1 className="mt-5 font-display font-bold text-4xl md:text-5xl text-navy-800 tracking-tight">
                {m.name}
              </h1>
              {m.role && <p className="mt-3 text-lg text-navy-700/75">{m.role}</p>}
              {m.qualifications && (
                <p className="mt-3 text-sm uppercase tracking-[0.18em] text-navy-500 font-semibold">
                  {m.qualifications}
                </p>
              )}
              {m.bio && (
                <div className="mt-6 prose prose-navy max-w-none" dangerouslySetInnerHTML={{ __html: m.bio }} />
              )}
              <div className="mt-8 flex flex-wrap gap-3">
                {m.email && (
                  <a href={`mailto:${m.email}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors">
                    <Mail className="h-4 w-4" /> Email
                  </a>
                )}
                {m.phone && (
                  <a href={`tel:${(m.phone || "").replace(/[\s\-()]/g, "")}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full ring-1 ring-navy/15 text-navy text-sm font-semibold hover:bg-navy hover:text-white transition-colors">
                    <Phone className="h-4 w-4" /> Call
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
